import get from 'lodash-es/get';
import { IdentityTeam, Location, TeamContact } from './index';

/**
 * Model to hold Team data for wrestling. It's used for grouping/categorizing
 * wrestlers together onto a single team
 */
export class Team {
    public isNew: boolean = true;
    public id: string = null;
    public eventId: string = null;
    public identityTeamId: string = null;
    public location: Location;
    public coaches: TeamContact[] = [];
    public resultEmails: TeamContact[] = [];
    public registeredWrestlerCount: number;
    public paid: boolean;
    public displayName: string = null;

    private _name: string = '';
    set name(name: string) {
        this._name = name && name.trim();
    }

    get name(): string {
        return this._name;
    }

    private _abbreviation: string = null;
    set abbreviation(abbreviation: string) {
        this._abbreviation = abbreviation && abbreviation.trim();
    }

    get abbreviation(): string {
        return this._abbreviation;
    }

    public static getFields(): string[] {
        return [
            'eventId',
            'identityTeamId',
            'location',
            'name',
            'abbreviation',
            'paid',
        ];
    }

    /**
     * Constructor
     */
    constructor(eventId: string, id: string = null, isNew: boolean = true) {
        this.eventId = eventId;
        this.location = new Location();
        this.id = id;
        this.isNew = isNew;
    }

    public formatName(): string {
        if (this.abbreviation) {
            return `${this.name} (${this.abbreviation})`;
        }

        return this.name;
    }

    public getFormattedLocation(): string {
        if (this.location) {
            return this.location.format();
        }

        return null;
    }

    /**
     * Clone Team object
     */
    public clone(): Team {
        const clone: Team = new Team(this.eventId);
        clone.isNew = this.isNew;
        clone.id = this.id;
        clone.name = this.name;
        clone.abbreviation = this.abbreviation;
        clone.identityTeamId = this.identityTeamId;
        clone.registeredWrestlerCount = this.registeredWrestlerCount;
        clone.paid = this.paid;

        if (this.location) {
            clone.location = this.location.clone();
        }

        if (this.coaches) {
            clone.coaches = this.coaches.map((c: TeamContact) => c.clone());
        }

        if (this.resultEmails) {
            clone.resultEmails = this.resultEmails.map((c: TeamContact) => c.clone());
        }

        return clone;
    }

    /**
     * Fill from a TeamIdentity object
     */
    public fillFromIdentityTeam(teamIdentity: IdentityTeam): Team {
        if (teamIdentity) {
            this.name = teamIdentity.name;
            this.abbreviation = teamIdentity.abbreviation;
            this.identityTeamId = teamIdentity.id;

            if (teamIdentity.location) {
                this.location = teamIdentity.location.clone();
            }
        }

        return this;
    }

    /**
     * Serialize from a json api format
     */
    public serialize(): string {
        const json: any = {};
        json.data = {};
        json.data.type = 'team';
        json.data.id = this.id;
        json.data.attributes = {};
        json.data.attributes.name = this.name;
        json.data.attributes.paid = this.paid;

        if (this.abbreviation) {
            json.data.attributes.abbreviation = this.abbreviation;
        }

        if (this.identityTeamId) {
            json.data.attributes.identityTeamGuid = this.identityTeamId;
        }

        if (this.location) {
            json.data.attributes.location = this.location.serialize();
        }

        return JSON.stringify(json);
    }

    /**
     * Deserialize to json api format
     */
    public deserialize(json: any, included?: any[]): Team {
        const data: any = json.data || json;
        const attrs: any = data.attributes;
        included = included || json.included || [];

        this.isNew = false;
        this.id = data.id;
        this.eventId = attrs.eventId;
        this.name = attrs.name;
        this.abbreviation = attrs.abbreviation;
        this.identityTeamId = attrs.identityTeamId;
        this.registeredWrestlerCount = attrs.registeredWrestlerCount;
        this.paid = attrs.paid;

        if (attrs.location) {
            this.location = (new Location()).deserialize(attrs.location);
        }

        const coachesList: any[] = get(data, 'relationships.coaches.data') || [];
        coachesList.forEach((x: any) => {
            const item: any = included.find((i: any) => i.id === x.id);

            if (item) {
                this.coaches.push((new TeamContact()).deserialize(item));
            }
        });

        const resultEmailList: any[] = get(data, 'relationships.resultEmails.data') || [];
        resultEmailList.forEach((x: any) => {
            const item: any = included.find((i: any) => i.id === x.id);

            if (item) {
                this.resultEmails.push((new TeamContact()).deserialize(item));
            }
        });

        return this;
    }
}
